import { useMemo } from "react";
import { ShieldCheck } from "lucide-react";

import { SettingsPage } from "@/components/v2/settings-page";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PermissionsGrid } from "@/components/permissions-grid";
import { useCurrentUser } from "@/hooks/use-current-user";
import {
  ROLE_PRESETS,
  buildAbilityForPreset,
  type PresetAbilityScope,
  type RolePreset,
} from "@/lib/role-ability-presets";

// ADV-881: Read-only capability matrix for every role Adva supports today.
// The matrix is generated from defineAbilityFor() — if the ability rules
// change in @adva/auth, this page updates without code changes here.

export default function V2SettingsPermissionsPage() {
  const currentUser = useCurrentUser();

  const scope = useMemo<PresetAbilityScope>(
    () => ({
      accountId: currentUser?.accountId ?? "",
      businessId: currentUser?.businessId || null,
      userId: currentUser?.userId ?? null,
    }),
    [currentUser?.accountId, currentUser?.businessId, currentUser?.userId],
  );

  return (
    <SettingsPage
      breadcrumbs={[
        { label: "Settings" },
        { label: "Account" },
        { label: "Permissions" },
      ]}
      title="Permissions"
      subtitle="What each role can do, generated from the ability definitions in code."
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck
              className="h-4 w-4 text-[color:var(--color-text-md)]"
              aria-hidden
            />
            Capability matrix
          </CardTitle>
          <CardDescription>
            Pick a role to see the actions it grants on each subject. The grid
            is auto-generated from{" "}
            <code className="typo-mono">defineAbilityFor()</code> in{" "}
            <code className="typo-mono">@adva/auth</code> — when ability rules
            change in code, this view updates automatically. Per-user conditions
            (e.g. &ldquo;salespeople see only their own opportunities&rdquo;)
            are evaluated against your own user ID.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={ROLE_PRESETS[0].key} className="w-full">
            <TabsList className="w-full justify-start overflow-x-auto">
              {ROLE_PRESETS.map((preset) => (
                <TabsTrigger key={preset.key} value={preset.key}>
                  {preset.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {ROLE_PRESETS.map((preset) => (
              <RoleTab key={preset.key} preset={preset} scope={scope} />
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </SettingsPage>
  );
}

interface RoleTabProps {
  preset: RolePreset;
  scope: PresetAbilityScope;
}

function RoleTab({ preset, scope }: RoleTabProps) {
  // useMemo so re-rendering a sibling tab doesn't rebuild the ability for
  // every preset on every render. The ability is itself memoizable by
  // (systemRole, endUserRole, accountId, businessId, userId).
  const ability = useMemo(
    () => buildAbilityForPreset(preset, scope),
    [preset, scope],
  );

  return (
    <TabsContent value={preset.key} className="pt-6 space-y-4">
      <p className="typo-body text-[color:var(--color-text-md)]">
        {preset.description}
      </p>
      <PermissionsGrid ability={ability} scope={scope} />
    </TabsContent>
  );
}
